import {store} from '../store';
import {Environment, startModalLoading, stopModalLoading, updateEnvironment} from '../reducers/environmentReducer';
import {PeachPayOrder} from '../reducers/peachPayOrderReducer';
import {type LoadingMode, type ModalPage} from '../models/IEnvironment';
import {MerchantConfiguration} from '../reducers/merchantConfigurationReducer';
import {Carts, DefaultCart} from '../reducers/cartReducer';
import {getLocaleText} from '../util/translation';
import {renderModalPage} from './modalPage';
import {$qs, $qsAll, isMobile} from '../../../@shared/ts/dom';
import {closeCheckout} from '../sdk';
import {type CheckoutData} from '../models/CheckoutData';
import {validateCheckout} from './fields/validation';
import {doAction} from '../../../@shared/ts/hooks';

export function initModal(checkoutData: CheckoutData) {
	// Keep modal state rendering up to date
	let prevCurrencyCode = '';
	store.subscribe(() => {
		renderModalPageIndicator(Environment.modalUI.page());
		renderModalNavigation(Environment.modalUI.page());
		renderContinueButtonDisplay(Environment.modalUI.page());
		renderContinueButtonLoading(Environment.modalUI.loadingMode());
		ppDisableLoadingMode(Environment.modalUI.loadingMode());
		ppBlurOnRecalculate(
			Environment.modalUI.loadingMode(),
			prevCurrencyCode !== MerchantConfiguration.currency.code(),
		);
		prevCurrencyCode = MerchantConfiguration.currency.code();

		renderModalPage(Environment.modalUI.page());
		renderTermsAndCondition(
			Environment.modalUI.page(),
			checkoutData?.wc_terms_conditions ?? '',
		);
		renderHideOnMobile(isMobile());

		renderFreeOrderDisplay(DefaultCart.contents().length, Carts.total());
		renderSummaryBorder();
	});

	document.addEventListener('click', async e => {
		const $target = (e.target as HTMLElement)?.closest<HTMLElement>('[data-goto-page]');
		if (!$target) {
			return;
		}

		e.preventDefault();

		const page = $target.dataset['gotoPage'] as ModalPage;

		store.dispatch(startModalLoading());
		await gotoPage(page);
		store.dispatch(stopModalLoading());
	});

	$qsAll('.pp-close', $el => {
		$el.addEventListener('click', e => {
			e.preventDefault();
			closeCheckout();
		});
	});
}

/**
 * Hides the prices by bluring them until we can show the real amount they will be
 *
 * @param loadingMode The current loading state of the modal
 */
function ppBlurOnRecalculate(
	loadingMode: LoadingMode,
	currencyChanged: boolean,
) {
	if (loadingMode === 'loading') {
		$qsAll('.pp-recalculate-blur', element => {
			element.classList.add('pp-blur');
		});
		// Currently this does not visibily work but requires shipping.ts refactor
		if (currencyChanged) {
			$qsAll('.pp-currency-blur', element => {
				element.classList.add('pp-blur');
			});
		}
	} else {
		$qsAll('.pp-recalculate-blur', element => {
			element.classList.remove('pp-blur');
		});
		$qsAll('.pp-currency-blur', element => {
			element.classList.remove('pp-blur');
		});
	}
}

function renderFreeOrderDisplay(cartCount: number, allCartsTotal: number) {
	if (cartCount > 0 && allCartsTotal === 0) {
		$qsAll('.pp-hide-on-free-order', $el => {
			$el.classList.add('hide');
		});
	} else {
		$qsAll('.pp-hide-on-free-order', $el => {
			$el.classList.remove('hide');
		});
	}
}

function renderContinueButtonDisplay(modalPage: ModalPage) {
	if (modalPage === 'billing') {
		$qs('#pp-continue-to-shipping-mobile')?.classList.remove('hide');
		$qs('#pp-continue-to-payment-mobile')?.classList.add('hide');
		$qs('.pay-button-container-mobile')?.classList.add('hide');
	} else if (modalPage === 'shipping') {
		$qs('#pp-continue-to-shipping-mobile')?.classList.add('hide');
		$qs('#pp-continue-to-payment-mobile')?.classList.remove('hide');
		$qs('.pay-button-container-mobile')?.classList.add('hide');
	} else if (modalPage === 'payment') {
		$qs('#pp-continue-to-shipping-mobile')?.classList.add('hide');
		$qs('#pp-continue-to-payment-mobile')?.classList.add('hide');
		$qs('.pay-button-container-mobile')?.classList.remove('hide');
	}
}

function renderContinueButtonLoading(loadingMode: LoadingMode) {
	if (loadingMode === 'loading') {
		$qs('#continue-spinner-shipping')?.classList.remove('hide');
		$qs('#continue-spinner-payment')?.classList.remove('hide');
		$qs('#continue-spinner-shipping-mobile')?.classList.remove('hide');
		$qs('#continue-spinner-payment-mobile')?.classList.remove('hide');

		$qsAll('i.pp-icon-lock', $element => {
			$element.classList.add('hide');
		});
	} else {
		$qs('#continue-spinner-shipping')?.classList.add('hide');
		$qs('#continue-spinner-payment')?.classList.add('hide');
		$qs('#continue-spinner-shipping-mobile')?.classList.add('hide');
		$qs('#continue-spinner-payment-mobile')?.classList.add('hide');

		$qsAll('i.pp-icon-lock', $element => {
			$element.classList.remove('hide');
		});
	}
}

function ppDisableLoadingMode(loadingMode: LoadingMode) {
	if (loadingMode === 'loading') {
		$qsAll<HTMLInputElement>('.pp-disabled-loading', $element => {
			$element.classList.add('pp-disabled');
			$element.classList.add('pp-mode-loading');
		});
	} else if (loadingMode === 'processing') {
		$qsAll<HTMLInputElement>('.pp-disabled-processing', $element => {
			$element.classList.add('pp-disabled');
		});
	} else {
		$qsAll<HTMLInputElement>('.pp-disabled-processing,.pp-disabled-loading', $element => {
			$element.classList.remove('pp-disabled');
			$element.classList.remove('pp-mode-loading');
		});
	}
}

function renderHideOnMobile(isMobile: boolean) {
	if (isMobile) {
		$qsAll('.pp-hide-on-mobile', $el => {
			$el.classList.add('hide');
		});
	} else {
		$qsAll('.pp-hide-on-mobile', $el => {
			$el.classList.remove('hide');
		});
	}
}

/**
 * Renders the Terms and Conditions section above the pay button.
 */
function renderTermsAndCondition(page: ModalPage, terms?: string) {
	const merchantTermsConditions = terms ? `${getLocaleText('By completing the checkout, you agree to our')} <a href='${terms}' target='_blank'>${getLocaleText('terms and conditions')}</a>.` : '';
	if (page === 'payment' && merchantTermsConditions) {
		$qsAll('.pp-tc-section', $el => {
			$el.innerHTML = `<label class='pp-tc-contents'>${merchantTermsConditions}</label>`;
			$el.classList.remove('hide');
		});
	} else {
		$qsAll('.pp-tc-section', $el => {
			$el.classList.add('hide');
		});
	}
}

function renderSummaryBorder() {
	let itemsHeight = 0;
	$qs('#pp-summary-body', $parent => {
		const $children = $parent?.querySelectorAll('.pp-order-summary-item');
		if (!$children) {
			return;
		}

		for (const $child of Array.from($children)) {
			itemsHeight += $child?.clientHeight ?? 0;
		}

		if ($parent?.clientHeight < itemsHeight) {
			$parent.classList.add('pp-review-border');
		} else {
			$parent.classList.remove('pp-review-border');
		}
	});
}

type ChangePageArguments = [fromPage: ModalPage, toPage: ModalPage];
async function gotoPage(toPage: ModalPage): Promise<boolean> {
	const changePage = async (currentPage: ModalPage, targetPage: ModalPage) => {
		$qs('.pp-close')?.scrollIntoView();

		await doAction(`before_${targetPage}_page`, currentPage, targetPage);

		store.dispatch(updateEnvironment({modalPageType: targetPage}));

		await doAction(`after_${targetPage}_page`, currentPage, targetPage);

		return true;
	};

	const fromPage = Environment.modalUI.page();
	switch (fromPage) {
		case 'billing': {
			if (!await PeachPayOrder.billing.reportValidity()) {
				break;
			}

			if (toPage === 'shipping') {// Info -> Shipping
				if (await validateCheckout('billing')) {
					if (shouldSkipShippingPage()) {
						return changePage(fromPage, 'payment');
					}

					return changePage(fromPage, toPage);
				}
			} else if (toPage === 'payment') {// Info -> Payment
				if (!PeachPayOrder.shipping.checkValidity()) {
					await PeachPayOrder.shipping.reportValidity();
					break;
				} else if (!PeachPayOrder.additional.checkValidity()) {
					await PeachPayOrder.shipping.reportValidity();
					break;
				}

				if (await validateCheckout('billing_shipping')) {
					return changePage(fromPage, toPage);
				}
			}

			break;
		}

		case 'shipping': {
			if (toPage === 'billing') {// Shipping -> Info
				return changePage(fromPage, toPage);
			}

			if (toPage === 'payment') {// Shipping -> Payment
				if (!PeachPayOrder.shipping.checkValidity()) {
					await PeachPayOrder.shipping.reportValidity();
					break;
				} else if (!PeachPayOrder.additional.checkValidity()) {
					await PeachPayOrder.additional.reportValidity();
					break;
				}

				if (await validateCheckout('billing_shipping')) {
					return changePage(fromPage, toPage);
				}
			}

			break;
		}

		case 'payment': {
			if (toPage === 'billing') {// Payment -> Info
				return changePage(fromPage, toPage);
			}

			if (toPage === 'shipping') {// Payment -> Shipping
				if (shouldSkipShippingPage()) {
					return changePage(fromPage, 'billing');
				}

				return changePage(fromPage, toPage);
			}

			break;
		}
	}

	return false;
}

async function maybeGotoPage(desiredPage: ModalPage): Promise<boolean> {
	const currentPage = Environment.modalUI.page();
	if (currentPage !== desiredPage) {
		await gotoPage(desiredPage);
	}

	return true;
}

function renderModalPageIndicator(modalPage: ModalPage): void {
	// Show/hide info page indicator
	if (modalPage === 'billing') {
		// Info tab
		$qs('#pp-billing-tab')?.classList.add('current');
		$qs('#pp-billing-tab-text')?.classList.add('hide');

		// Shipping/additional tab
		$qs('#pp-shipping-tab')?.classList.remove('current');
		$qs('#pp-shipping-tab')?.classList.add('no-fill');
		$qs('#pp-shipping-tab-text')?.classList.add('hide');

		// Payment tab
		$qs('#pp-payment-tab')?.classList.remove('current');
		$qs('#pp-payment-tab')?.classList.add('no-fill');
	} else if (modalPage === 'shipping') {
		// Info tab
		$qs('#pp-billing-tab')?.classList.remove('current');
		$qs('#pp-billing-tab-text')?.classList.remove('hide');

		// Shipping/additional tab
		$qs('#pp-shipping-tab')?.classList.remove('no-fill');
		$qs('#pp-shipping-tab')?.classList.add('current');
		$qs('#pp-shipping-tab-text')?.classList.add('hide');

		// Payment tab
		$qs('#pp-payment-tab')?.classList.remove('current');
		$qs('#pp-payment-tab')?.classList.add('no-fill');
	} else if (modalPage === 'payment') {
		// Info tab
		$qs('#pp-billing-tab')?.classList.remove('current');
		$qs('#pp-billing-tab-text')?.classList.remove('hide');

		// Shipping/additional tab
		$qs('#pp-shipping-tab')?.classList.remove('current');
		$qs('#pp-shipping-tab')?.classList.remove('no-fill');
		$qs('#pp-shipping-tab-text')?.classList.remove('hide');

		// Payment tab
		$qs('#pp-payment-tab')?.classList.add('current');
		$qs('#pp-payment-tab')?.classList.remove('no-fill');
	}

	if (shouldSkipShippingPage()) {
		$qs('#pp-shipping-tab')?.classList.add('hide');
		$qs('#pp-shipping-page-message')?.classList.remove('hide');
	} else {
		$qs('#pp-shipping-tab')?.classList.remove('hide');
		$qs('#pp-shipping-page-message')?.classList.add('hide');
	}
}

function renderModalNavigation(modalPage: ModalPage) {
	if (modalPage === 'billing') {
		$qs('#pp-exit-mobile')?.classList.remove('hide');
		$qs('#pp-back-to-info-mobile')?.classList.add('hide');
		$qs('#pp-back-to-shipping-mobile')?.classList.add('hide');
	} else if (modalPage === 'shipping') {
		$qs('#pp-exit-mobile')?.classList.add('hide');
		$qs('#pp-back-to-info-mobile')?.classList.remove('hide');
		$qs('#pp-back-to-shipping-mobile')?.classList.add('hide');
	} else if (modalPage === 'payment') {
		$qs('#pp-exit-mobile')?.classList.add('hide');
		$qs('#pp-back-to-info-mobile')?.classList.add('hide');
		$qs('#pp-back-to-shipping-mobile')?.classList.remove('hide');
	}
}

function shouldSkipShippingPage() {
	return !Carts.needsShipping() && !$qs('pp-additional-form');
}

export {
	type ChangePageArguments,

	gotoPage,
	maybeGotoPage,
};
